import { Component, Input, ViewChild, ContentChildren, AfterContentInit, AfterViewInit, QueryList, TemplateRef, EventEmitter, Output } from '@angular/core';

import { Table, TableLazyLoadEvent, TableRowSelectEvent, TableRowUnSelectEvent } from 'primeng/table';

import { AppContext } from '../../app.context';
import { AppInsightsService } from '../../services/app-insights.service';

import { FilterDataEvent, GridColumn, GridFilterMatchModes, IGridRow, ShowFilterEvent, SortDataEvent } from '../grid.model';
import { GridProcessor } from '../grid.processor';
import { OverlayPanel } from 'primeng/overlaypanel/public_api';
import { fieldof } from '../string-operations';
import { SortMeta, LazyLoadEvent, MenuItem, PrimeTemplate, FilterService } from 'primeng/api';

@Component({
  selector: 'app-grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.scss']
})
export class GridComponent implements AfterContentInit, AfterViewInit {
  @ViewChild('table', { static: false }) readonly table: Table;
  @ViewChild('globalFilter', { static: false }) filterInput: any;

  @Input() tableId: string;
  @Input() colSelectId: string;
  @Input() resetFilterId: string;
  @Input() loading: boolean;
  @Input() zeroRecordsFoundMessage: string;

  @Input() cols: Array<GridColumn>;
  @Input() data: Array<IGridRow>;

  @Input() isMultiSelect: boolean;
  @Input() selectionMode: SelectionMode;
  @Input() selectedColumns: Array<GridColumn>;
  @Input() availableColumns: Array<GridColumn>;
  @Input() globalFilterFields: Array<string>;
  @Input() multiSortMeta: Array<SortMeta>;

  @Input() lazy = false;
  @Input() lazyLoadOnInit = true;
  @Input() totalRecords: number = null;
  @Input() totalColumns: number = null;

  @Input() selection: Array<IGridRow>;
  @Input() paginator = true;
  @Input() rowsPerPageOverride: Array<number>;
  @Input() useUniqueFilterId = false;
  @Output() selectionChange = new EventEmitter<Array<IGridRow>>();

  @Input() multiSelectActionMenuItems: Array<MenuItem>;

  @Output() lazyLoad = new EventEmitter<TableLazyLoadEvent>();
  @Output() sort = new EventEmitter<SortDataEvent>();
  @Output() showFilter = new EventEmitter<ShowFilterEvent>();
  @Output() triggerRefresh = new EventEmitter();
  @Output() rowSelect = new EventEmitter<TableRowSelectEvent>();
  @Output() rowUnselect = new EventEmitter<TableRowUnSelectEvent>();
  @Output() filterData = new EventEmitter<FilterDataEvent>();
  @Output() resetFilterData = new EventEmitter<FilterDataEvent>();

  @Input() showCaption: boolean = true;

  captionLeftContentPrefix: TemplateRef<any>;
  captionLeftContent: TemplateRef<any>;
  captionLeftContentSuffix: TemplateRef<any>;

  captionRightContentPrefix: TemplateRef<any>;
  captionRightContent: TemplateRef<any>;
  captionRightContentSuffix: TemplateRef<any>;

  colGroupPrefix: TemplateRef<any>;
  colGroupContent: TemplateRef<any>;
  colGroupSuffix: TemplateRef<any>;

  headerRowPrefix: TemplateRef<any>;
  headerRowContentPrefix: TemplateRef<any>;
  headerRowContent: TemplateRef<any>;
  headerRowContentSuffix: TemplateRef<any>;
  headerRowSuffix: TemplateRef<any>;

  bodyRowContentPrefix: TemplateRef<any>;
  bodyRowContent: TemplateRef<any>;
  bodyRowContentSuffix: TemplateRef<any>;

  paginatorLeftContentPrefix: TemplateRef<any>;
  paginatorLeftContent: TemplateRef<any>;
  paginatorLeftContentSuffix: TemplateRef<any>;

  paginatorRightContentPrefix: TemplateRef<any>;
  paginatorRightContent: TemplateRef<any>;
  paginatorRightContentSuffix: TemplateRef<any>;

  footerContent: TemplateRef<any>;

  @ContentChildren(PrimeTemplate) templates: QueryList<PrimeTemplate>;

  overlayPanel: OverlayPanel = null;

  rowsPerPageOptions: Array<number>;

  uniqueId: string = "";

  searchTimeout: any = null;
  constructor(
    readonly appContext: AppContext,
    readonly filterService: FilterService,
    readonly appInsightsService: AppInsightsService) {
    this.rowsPerPageOptions = [10, 20, 50, 100];
    GridProcessor.registerCustomFilters(filterService);
  }

  ngOnInit(): void {
    if (this.rowsPerPageOverride) this.rowsPerPageOptions = this.rowsPerPageOverride;
    if (this.useUniqueFilterId)
      this.uniqueId = this.tableId;
  }

  ngAfterContentInit(): void {
    this.templates.forEach((item) => {
      switch (item.getType()) {
        case fieldof<GridComponent>('captionLeftContentPrefix'):
          this.captionLeftContentPrefix = item.template;
          break;
        case fieldof<GridComponent>('captionLeftContent'):
          this.captionLeftContent = item.template;
          break;
        case fieldof<GridComponent>('captionLeftContentSuffix'):
          this.captionLeftContentSuffix = item.template;
          break;
        case fieldof<GridComponent>('captionRightContentPrefix'):
          this.captionRightContentPrefix = item.template;
          break;
        case fieldof<GridComponent>('captionRightContent'):
          this.captionRightContent = item.template;
          break;
        case fieldof<GridComponent>('captionRightContentSuffix'):
          this.captionRightContentSuffix = item.template;
          break;
        case fieldof<GridComponent>('colGroupPrefix'):
          this.colGroupPrefix = item.template;
          break;
        case fieldof<GridComponent>('colGroupContent'):
          this.colGroupContent = item.template;
          break;
        case fieldof<GridComponent>('colGroupSuffix'):
          this.colGroupSuffix = item.template;
          break;
        case fieldof<GridComponent>('headerRowPrefix'):
          this.headerRowPrefix = item.template;
          break;
        case fieldof<GridComponent>('headerRowContentPrefix'):
          this.headerRowContentPrefix = item.template;
          break;
        case fieldof<GridComponent>('headerRowContent'):
          this.headerRowContent = item.template;
          break;
        case fieldof<GridComponent>('headerRowContentSuffix'):
          this.headerRowContentSuffix = item.template;
          break;
        case fieldof<GridComponent>('headerRowSuffix'):
          this.headerRowSuffix = item.template;
          break;
        case fieldof<GridComponent>('bodyRowContentPrefix'):
          this.bodyRowContentPrefix = item.template;
          break;
        case fieldof<GridComponent>('bodyRowContent'):
          this.bodyRowContent = item.template;
          break;
        case fieldof<GridComponent>('bodyRowContentSuffix'):
          this.bodyRowContentSuffix = item.template;
          break;
        case fieldof<GridComponent>('paginatorLeftContentPrefix'):
          this.paginatorLeftContentPrefix = item.template;
          break;
        case fieldof<GridComponent>('paginatorLeftContent'):
          this.paginatorLeftContent = item.template;
          break;
        case fieldof<GridComponent>('paginatorLeftContentSuffix'):
          this.paginatorLeftContentSuffix = item.template;
          break;
        case fieldof<GridComponent>('paginatorRightContentPrefix'):
          this.paginatorRightContentPrefix = item.template;
          break;
        case fieldof<GridComponent>('paginatorRightContent'):
          this.paginatorRightContent = item.template;
          break;
        case fieldof<GridComponent>('paginatorRightContentSuffix'):
          this.paginatorRightContentSuffix = item.template;
          break;
        case fieldof<GridComponent>('footerContent'):
          this.footerContent = item.template;
          break;
      }
    });

    this.updateGlobalFilterFields();
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    if (this.table) {
      if (!this.table.multiSortMeta)
        this.table.multiSortMeta = this.multiSortMeta;
      if (!event.multiSortMeta)
        event.multiSortMeta = this.table.multiSortMeta;
    }

    this.lazyLoad.emit(event);
  }

  onRowSelect(event: TableRowSelectEvent): void {
    this.rowSelect.emit(event);
  }

  onRowUnselect(event: TableRowUnSelectEvent): void {
    this.rowUnselect.emit(event);
  }

  ngAfterViewInit(): void {
    // TODO: remove set aria-expanded attribute once primeng team fixes the known issue.
    // Bug created: https://github.com/primefaces/primeng/issues/14832
    // set attribute on paginator dropdown element generated via primeNg for accessibility purposes
    const paginatorDropDown = document.getElementsByClassName('p-paginator-rpp-options')

    if (paginatorDropDown && paginatorDropDown.length) {
      const firstHiddenElement = paginatorDropDown[0];
      if (firstHiddenElement.children.length) {
        firstHiddenElement.children[0].setAttribute('aria-expanded', 'false');
      }
    }
    // set attribute on hidden input element generated via primeNg for accessibility purposes
    const element = document.getElementById(this.colSelectId);
    if (!element) return;

    const hiddenElements = element.getElementsByClassName('ui-helper-hidden-accessible');
    if (hiddenElements && hiddenElements.length) {
      const firstHiddenElement = hiddenElements[0];
      if (firstHiddenElement.children.length) {
        firstHiddenElement.children[0].setAttribute('type', 'hidden');
      }
    }
  }

  updateGlobalFilterFields(): void {
    this.globalFilterFields = this.selectedColumns.map(col => col.field);
  }

  extractColumnFilters(): void {
    GridProcessor.extractColumnFilters(this.cols, this.data);
  }

  filter(value: any, col: GridColumn): void {
    if (col.filter && col.filter.delay) {
      clearTimeout(this.searchTimeout);
      const $this = this;
      this.searchTimeout = setTimeout(function () {
        $this.executeFilter(value, col);
      }, col.filter.delay);
    } else
      this.executeFilter(value, col);
  }

  private executeFilter(value: any, col: GridColumn) {
    this.table.filter(
      value,
      (col.filter && col.filter.field) || col.field,
      (col.filter && col.filter.matchMode) || GridFilterMatchModes.in);
    if (!this.lazy) {
      this.extractColumnFilters();
    }
  }

  filterGlobal(value: any): void {
    this.table.filterGlobal(value, GridFilterMatchModes.contains);
    if (!this.lazy) {
      this.extractColumnFilters();
    }
  }

  reset(): void {
    this.table.first = 0;
    this.table.multiSortMeta = this.multiSortMeta;
    if (this.filterInput) {
      this.filterInput.nativeElement.value = '';
    }
    GridProcessor.resetFilters(this.cols, col => this.filter(null, col));
    this.table.filters = {};

    this.refresh();
  }

  refresh(): void {
    if (this.lazy)
      this.table.sortMultiple();
    else
      this.extractColumnFilters();

    this.filterData.emit(new FilterDataEvent({
      filters: {},
      filteredData: this.data
    }));
  }

  //
  // Control tabbing into overlay panel
  tabHandler(event: any, col: GridColumn): void {
    if (this.overlayPanel && this.overlayPanel.show) {
      event.preventDefault();
      this.overlayPanel.el.nativeElement.focus();
      const el = document.getElementById(col.field + 'FilterContainer');
      el.focus();
    }
  }

  //
  // Tab out of overlay panel
  shiftTabHandler(event: any, col: GridColumn): void {
    if (event.shiftKey && event.key === 'Tab') {
      if (event.target.id.includes('FilterContainer')) {
        const el = document.getElementById(col.field + 'FilterButton');
        el.focus();
      }
    }
  }

  //
  // manually handle toggle to update main overlay panel context
  toggleOverlayPanel(op: OverlayPanel, event: any): void {
    if (!this.overlayPanel || !this.overlayPanel.show) {
      this.overlayPanel = op;
    } else if (this.overlayPanel.el.nativeElement.id !== op.el.nativeElement.id) {
      this.overlayPanel.hide();
      this.overlayPanel = op;
    }
    op.toggle(event);
  }

  //
  // Callback to raise showFilter event in response to overlay panel being shown
  showOverlayPanel(event: any, col: GridColumn): void {
    if (this.lazy) {
      const lazyLoadMetadata = this.table.createLazyLoadMetadata() as LazyLoadEvent;
      this.showFilter.emit(new ShowFilterEvent({
        col: col,
        filters: lazyLoadMetadata.filters,
        globalFilter: lazyLoadMetadata.globalFilter
      }));
    }
  }

  onSelectionChange(event: Array<IGridRow>): void {
    this.selectionChange.emit(event);
  }

  onFilter(event: any) {
    this.filterData.emit(new FilterDataEvent({
      filters: event.filters,
      filteredData: event.filteredValue
    }));
  }

  onSort(event: any) {
    this.sort.emit(event);
  }
}
